// ============================================================================
// Catálogo de abas de colaborador (espelho das abas do Master)
// ----------------------------------------------------------------------------
// Fonte única de verdade do que o Master pode liberar para cada CARGO
// (employee_types). Cada aba aqui corresponde a uma tela já existente de
// colaborador — que por sua vez espelha uma aba do Master — e descreve quais
// colunas e filtros aquela tela suporta.
//
// O Master escolhe, por cargo: quais abas aparecem (e em que ordem) e, dentro
// de cada aba, quais colunas/filtros ficam visíveis. Nada disso fica hardcoded
// na navegação: a config vive no documento de cada cargo, em `tabs`.
//
// Formato de `employee_types/<doc>.tabs` (array ordenado):
//   [
//     { key: "projects", columns: ["projectNumber", ...], filters: ["payment"] },
//     { key: "ongoing",  columns: [...],                   filters: [...] },
//   ]
// - A ORDEM do array define a ordem das abas no menu lateral.
// - `columns`/`filters` ausentes => usa o default da aba (defaultColumns/...).
// - Colunas marcadas como `fixed` são sempre exibidas (não podem ser desligadas).
// ============================================================================

import { PERFORMANCE_CARDS } from "./dashboardCatalog";

// Conjuntos de colunas reutilizados (espelham as tabelas reais das páginas).

// Colunas no nível de PROJETO (ProjectManagerProjects / ResourceManagerOnGoing).
const PROJECT_COLUMNS = [
  { id: "projectNumber", label: "Nº", fixed: true },
  { id: "client", label: "Cliente" },
  { id: "nickname", label: "Apelido" },
  { id: "projectName", label: "Projeto" },
  { id: "createdAt", label: "Data" },
  { id: "sourceLanguage", label: "Origem" },
  { id: "targetLanguage", label: "Destino" },
  { id: "pages", label: "Páginas" },
  { id: "filesDisplay", label: "Arquivos" },
  { id: "totalValue", label: "Valor" },
  { id: "paymentStatus", label: "Pagamento" },
  { id: "deadline", label: "Prazo" },
  { id: "clientType", label: "Tipo" },
  { id: "projectStatus", label: "Status" },
  { id: "translationStatus", label: "Tradução" },
];

// Colunas no nível de ARQUIVO (ResourceManagerFilesAll / FilesDone).
const FILE_COLUMNS = [
  { id: "projectNumber", label: "Nº" },
  { id: "projectName", label: "Projeto" },
  { id: "fileName", label: "Arquivo" },
  { id: "createdAt", label: "Data" },
  { id: "sourceLanguage", label: "Origem" },
  { id: "targetLanguage", label: "Destino" },
  { id: "pages", label: "Páginas" },
  { id: "pdfType", label: "PDF" },
  { id: "projectStatus", label: "Status" },
  { id: "translationStatus", label: "Tradução" },
];

// Colunas da planilha "Andamento por Arquivo" (MasterAndamentoArquivos). Além
// das estruturais, "stages" representa o BLOCO de colunas dinâmicas (uma por
// etapa do workflow). O Nº do projeto é fixo (sempre visível).
const ANDAMENTO_ARQUIVOS_COLUMNS = [
  { id: "projectNumber", label: "Projeto", fixed: true },
  { id: "progress", label: "Progresso" },
  { id: "status", label: "Status" },
  { id: "client", label: "Cliente" },
  { id: "fileName", label: "Arquivo" },
  { id: "pageCount", label: "Pág" },
  { id: "conversao", label: "Conversão (moeda)" },
  { id: "cotacao", label: "Cotação do dia (BRL)" },
  { id: "pdf", label: "PDF (nativo/escaneado)" },
  { id: "view", label: "Ver" },
  { id: "stages", label: "Etapas (colunas do workflow)" },
];

// Colunas da tabela de PROJETOS de "Andamento por Projeto" (MasterAndamento).
// "stages" é o bloco de colunas dinâmicas (uma por etapa). Nº do projeto é fixo.
const ANDAMENTO_COLUMNS = [
  { id: "projectNumber", label: "Projeto", fixed: true },
  { id: "client", label: "Cliente" },
  { id: "files", label: "Arquivos" },
  { id: "pages", label: "Páginas" },
  { id: "deadline", label: "Prazo" },
  { id: "status", label: "Status" },
  { id: "missing", label: "Falta atribuir" },
  { id: "stages", label: "Etapas (colunas do workflow)" },
  { id: "progress", label: "Progresso" },
];

// Coluna de entrega dos arquivos finalizados/certificados (espelha a coluna
// "Finalizados" de MasterProjects). Fica no pool de colunas para que o Master
// possa liberar POR CARGO quem pode ver/baixar os arquivos entregues ao cliente.
const FINALIZED_COLUMN = { id: "finalized", label: "Finalizados" };

// Filtros disponíveis (espelham os FILTER_OPTIONS das páginas).
const SOURCE_FILTER = { id: "sourceLanguage", label: "Idioma de origem" };
const PAYMENT_FILTER = { id: "payment", label: "Pagamento" };
const CLIENT_TYPE_FILTER = { id: "clientType", label: "Tipo de cliente" };
const PROJECT_STATUS_FILTER = { id: "projectStatus", label: "Status do projeto" };
const TRANSLATION_STATUS_FILTER = { id: "translationStatus", label: "Status de tradução" };

// ============================================================================
// O CATÁLOGO
// ============================================================================
// Ordem aqui = ordem em que as abas aparecem na UI do Master para seleção.
export const EMPLOYEE_TAB_CATALOG = [
  {
    key: "projects",
    route: "/employee/projects",
    label: "Todos Projetos",
    icon: "FaProjectDiagram",
    // espelha /company/master/projects
    // Inclui a coluna "Finalizados" no pool (opt-in): o Master libera por cargo.
    columns: [...PROJECT_COLUMNS, FINALIZED_COLUMN],
    filters: [
      SOURCE_FILTER,
      PAYMENT_FILTER,
      CLIENT_TYPE_FILTER,
      PROJECT_STATUS_FILTER,
      TRANSLATION_STATUS_FILTER,
    ],
    // Igual ao DEFAULT_VISIBLE atual de ProjectManagerProjects.
    defaultColumns: [
      "projectNumber", "client", "nickname", "projectName", "createdAt",
      "sourceLanguage", "targetLanguage", "pages", "filesDisplay",
      "totalValue", "paymentStatus", "deadline", "projectStatus", "translationStatus",
    ],
  },
  // --- Sublinks do menu "Projetos" do Master que reusam os componentes do
  //     Master (mesmos dados/ações). Sem colunas/filtros configuráveis por ora:
  //     liga/desliga a aba inteira. ---
  { key: "drafts", route: "/employee/drafts", label: "Rascunhos", icon: "FaFileAlt", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "projects-budget", route: "/employee/projects-budget", label: "Aguardando Orçamento", icon: "FaFileInvoiceDollar", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "projects-approval", route: "/employee/projects-approval", label: "Aguardando Aprovação", icon: "FaCheckCircle", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "projects-in-analysis", route: "/employee/projects-in-analysis", label: "Em Análise", icon: "FaSearch", columns: [], filters: [], defaultColumns: [], mastered: true },
  {
    key: "ongoing",
    route: "/employee/ongoing",
    label: "Em Andamento",
    icon: "FaClock",
    // espelha /company/master/ongoing
    columns: PROJECT_COLUMNS,
    filters: [SOURCE_FILTER, PAYMENT_FILTER, TRANSLATION_STATUS_FILTER],
    // Igual ao DEFAULT_VISIBLE atual de ResourceManagerOnGoing.
    defaultColumns: [
      "projectNumber", "createdAt", "sourceLanguage", "targetLanguage",
      "pages", "paymentStatus", "deadline", "projectStatus", "translationStatus",
    ],
  },
  { key: "projects-done", route: "/employee/projects-done", label: "Concluídos", icon: "FaCheckDouble", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "projects-paid", route: "/employee/projects-paid", label: "Pagos", icon: "FaDollarSign", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "projects-cancelled", route: "/employee/projects-cancelled", label: "Cancelados", icon: "FaTimesCircle", columns: [], filters: [], defaultColumns: [], mastered: true },
  // Projetos da API EXTERNA finalizados+certificados cuja entrega ainda está
  // travada (não pagos e não liberados). Liga/desliga a aba inteira; a AÇÃO de
  // liberar continua separada, na permissão `releaseWithoutPayment` de Detalhes
  // do Projeto.
  { key: "pending-release", route: "/employee/pending-release", label: "Aguardando Liberação", icon: "FaLockOpen", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "payments", route: "/employee/payments", label: "Pagamentos Pendentes", icon: "FaMoneyBillWave", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "refunds", route: "/employee/refunds", label: "Para Reembolsos", icon: "FaUndo", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "add-project", route: "/employee/add-project", label: "Criar Projeto", icon: "FaPlus", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "workflow", route: "/employee/workflow", label: "Editar Workflow", icon: "FaProjectDiagram", columns: [], filters: [], defaultColumns: [], mastered: true },
  {
    key: "all-files",
    route: "/employee/all-files",
    label: "Todos Arquivos",
    icon: "FaFileAlt",
    // espelha /company/master/files/all
    columns: [
      ...FILE_COLUMNS,
      { id: "workflow", label: "Workflow" },
    ],
    filters: [SOURCE_FILTER, PROJECT_STATUS_FILTER, TRANSLATION_STATUS_FILTER],
    defaultColumns: [
      "projectNumber", "projectName", "fileName", "createdAt",
      "sourceLanguage", "targetLanguage", "pages", "pdfType",
      "projectStatus", "translationStatus", "workflow",
    ],
  },
  { key: "files-ongoing", route: "/employee/files-ongoing", label: "Arquivos em Andamento", icon: "FaSpinner", columns: [], filters: [], defaultColumns: [], mastered: true },
  { key: "task-management", route: "/employee/task-management", label: "Gestão de Tarefas", icon: "FaTasks", columns: [], filters: [], defaultColumns: [], mastered: true },
  // --- Sublinks do menu "Arquivos" do Master (andamento/desempenho). Reusam os
  //     componentes do Master; liga/desliga a aba inteira. ---
  {
    key: "andamento",
    route: "/employee/andamento",
    label: "Andamento por Projeto",
    icon: "FaChartLine",
    // O Master define quais colunas da tabela de projetos o cargo pode ver.
    columns: ANDAMENTO_COLUMNS,
    filters: [],
    defaultColumns: [
      "projectNumber", "client", "files", "pages", "deadline",
      "status", "missing", "stages", "progress",
    ],
    mastered: true,
  },
  {
    key: "andamento-arquivos",
    route: "/employee/andamento-arquivos",
    label: "Andamento por Arquivo",
    icon: "FaStream",
    // O Master define quais colunas desta planilha o cargo pode ver.
    columns: ANDAMENTO_ARQUIVOS_COLUMNS,
    filters: [],
    defaultColumns: [
      "projectNumber", "progress", "status", "client", "fileName",
      "pageCount", "conversao", "cotacao", "pdf", "view", "stages",
    ],
  },
  {
    key: "job-queue",
    route: "/employee/job-queue",
    label: "Fila de Jobs",
    icon: "FaListOl",
    // Página de fila de jobs por colaborador. Reordenar exige a capacidade
    // controlQueue; sem ela, é só leitura.
    columns: [],
    filters: [],
    defaultColumns: [],
    mastered: true,
  },
  {
    key: "employee-performance",
    route: "/employee/employee-performance",
    label: "Gestão de Desempenho",
    icon: "FaChartBar",
    // Página de equipe (MasterEmployeeDashboard). O Master escolhe quais CARDS
    // aparecem (mesmos blocos do Dashboard de Desempenho).
    columns: [],
    filters: [],
    defaultColumns: [],
    cards: PERFORMANCE_CARDS,
    defaultCards: PERFORMANCE_CARDS.map((b) => b.id),
    mastered: true,
  },
  {
    key: "productivity",
    route: "/employee/productivity",
    label: "Produtividade",
    icon: "FaChartBar",
    // Quadro de páginas produzidas por colaborador × etapa, por mês/período
    // (MasterProductivity). Visão de EQUIPE inteira (igual ao Master). Liga/
    // desliga a aba inteira; sem colunas/filtros configuráveis.
    columns: [],
    filters: [],
    defaultColumns: [],
    mastered: true,
  },
  {
    key: "done-files",
    route: "/employee/done-files",
    label: "Arquivos Concluídos",
    icon: "FaCheckDouble",
    // espelha /company/master/files/done
    columns: FILE_COLUMNS,
    filters: [SOURCE_FILTER, TRANSLATION_STATUS_FILTER],
    defaultColumns: [
      "projectNumber", "projectName", "fileName", "createdAt",
      "sourceLanguage", "targetLanguage", "pages", "pdfType",
      "projectStatus", "translationStatus",
    ],
  },
  {
    key: "performance",
    route: "/employee/performance",
    label: "Meu Desempenho",
    icon: "FaChartLine",
    // Dashboard de desempenho do PRÓPRIO colaborador (espelha o dashboard que o
    // Master vê em Andamento → clicar no colaborador). Liga/desliga a aba
    // inteira; sem colunas/filtros. O colaborador vê só as próprias métricas.
    columns: [],
    filters: [],
    defaultColumns: [],
    mastered: true,
  },
  {
    key: "files",
    route: "/employee/files",
    label: "Meus Arquivos / Etapas",
    icon: "FaInbox",
    // Tela do linguista/editor: lista os arquivos do workflow atribuídos a este
    // colaborador e abre o editor bicolunado (CompareEdit) para trabalhar a
    // etapa. Ligue esta aba só para cargos que PARTICIPAM da edição de arquivos.
    // Sem colunas/filtros configuráveis.
    columns: [],
    filters: [],
    defaultColumns: [],
  },
];

// Mapa key -> definição da aba, para lookup rápido.
export const TAB_BY_KEY = EMPLOYEE_TAB_CATALOG.reduce((acc, tab) => {
  acc[tab.key] = tab;
  return acc;
}, {});

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

// Colunas (ids) que não podem ser desligadas para uma aba.
export const getFixedColumnIds = (tabKey) =>
  (TAB_BY_KEY[tabKey]?.columns || [])
    .filter((c) => c.fixed)
    .map((c) => c.id);

// Cards (ids) configuráveis de uma aba (ex.: Gestão de Desempenho). Vazio se a
// aba não tem cards selecionáveis.
export const getTabCardIds = (tabKey) =>
  (TAB_BY_KEY[tabKey]?.cards || []).map((c) => c.id);

// Config default de uma aba (usada ao adicionar a aba a um cargo ou no seed).
export const getDefaultTabConfig = (tabKey) => {
  const def = TAB_BY_KEY[tabKey];
  if (!def) return null;
  const cfg = {
    key: tabKey,
    columns: [...(def.defaultColumns || [])],
    filters: (def.filters || []).map((f) => f.id),
  };
  if (def.cards) cfg.cards = [...(def.defaultCards || def.cards.map((c) => c.id))];
  return cfg;
};

// Normaliza a config de abas de um cargo, ignorando abas que não existem mais
// no catálogo e garantindo que colunas `fixed` estejam sempre presentes.
export const resolveRoleTabs = (tabs) => {
  if (!Array.isArray(tabs)) return [];
  return tabs
    .filter((t) => t && TAB_BY_KEY[t.key])
    .map((t) => {
      const def = TAB_BY_KEY[t.key];
      const fixed = getFixedColumnIds(t.key);
      const chosen = Array.isArray(t.columns) ? t.columns : def.defaultColumns;
      // mantém a ordem do catálogo; garante colunas fixas.
      const columns = def.columns
        .map((c) => c.id)
        .filter((id) => fixed.includes(id) || chosen.includes(id));
      const allowedFilters = def.filters.map((f) => f.id);
      const filters = Array.isArray(t.filters)
        ? t.filters.filter((id) => allowedFilters.includes(id))
        : allowedFilters;
      const out = {
        key: def.key,
        route: def.route,
        label: def.label,
        icon: def.icon,
        columns,
        filters,
      };
      if (def.cards) {
        const allowedCards = def.cards.map((c) => c.id);
        out.cards = Array.isArray(t.cards)
          ? t.cards.filter((id) => allowedCards.includes(id))
          : allowedCards;
      }
      return out;
    });
};

// Config-semente equivalente ao comportamento atual (hardcoded) de cada cargo.
// Usada uma única vez para não deixar colaboradores sem aba na virada.
export const SEED_ROLE_TABS = {
  gerente_projeto: [getDefaultTabConfig("projects")],
  resource_manager: [
    getDefaultTabConfig("ongoing"),
    getDefaultTabConfig("all-files"),
    getDefaultTabConfig("done-files"),
  ],
  linguista: [getDefaultTabConfig("files")],
  formatador: [getDefaultTabConfig("files")],
};
